import { useState, useEffect, useCallback, useMemo } from 'react';
import type { DoctorShift } from '../../../../types/doctorShift';
import type { DoctorShiftRepository } from '../../../../application/doctorShift/ports/DoctorShiftRepository';
import {
  fetchDoctorShiftsUseCase,
  createDoctorShiftUseCase,
  updateDoctorShiftUseCase,
  deleteDoctorShiftUseCase,
} from '../../../../application/doctorShift';
import type { OperationResult } from '../../../../application/doctorShift/ports/DoctorShiftRepository';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subDays,
  subMonths,
} from 'date-fns';
import type { ViewMode } from './useUrlState';

/**
 * フックの内部状態
 */
export interface MonthlyDoctorsShiftState {
  /** シフト一覧 */
  shifts: DoctorShift[];
  /** ローディング状態 */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** 編集中のシフト */
  editingShift: DoctorShift | null;
  /** ポップアップ表示状態 */
  isPopupOpen: boolean;
  /** 新規作成モード */
  isCreating: boolean;
}

/**
 * フックの戻り値型
 */
export interface UseMonthlyDoctorsShiftReturn extends MonthlyDoctorsShiftState {
  /** シフトデータを再取得 */
  fetchShifts: () => Promise<void>;
  /** 表示期間の日付配列を取得 */
  getDatesInRange: (selectedDate: Date, viewMode: ViewMode) => Date[];
  /** 次の期間へ移動時の日付を計算 */
  getNextDate: (currentDate: Date, viewMode: ViewMode) => Date;
  /** 前の期間へ移動時の日付を計算 */
  getPreviousDate: (currentDate: Date, viewMode: ViewMode) => Date;
  /** シフトバーをクリック（編集モード開始） */
  handleShiftClick: (shift: DoctorShift) => void;
  /** タイムライン空き領域をダブルクリック（新規作成モード開始） */
  handleTimelineDoubleClick: (date: string, time: string) => void;
  /** ポップアップを閉じる */
  closePopup: () => void;
  /** シフトを保存（作成/更新） */
  saveShift: (data: Partial<DoctorShift>) => Promise<boolean>;
  /** シフトを削除 */
  deleteShift: (id: string, version: number) => Promise<boolean>;
  /** エラーをクリア */
  clearError: () => void;
}

/**
 * 日付範囲を計算
 */
const calculateDateRange = (
  selectedDate: Date,
  viewMode: ViewMode
): { startDate: string; endDate: string } => {
  switch (viewMode) {
    case 'day':
      return {
        startDate: format(selectedDate, 'yyyy-MM-dd'),
        endDate: format(selectedDate, 'yyyy-MM-dd'),
      };
    case 'week':
    case 'slotSummaryWeek':
      return {
        startDate: format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        endDate: format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    case 'month':
    case 'slotSummaryMonth':
      return {
        startDate: format(startOfMonth(selectedDate), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(selectedDate), 'yyyy-MM-dd'),
      };
  }
};

/**
 * 月次医師シフト管理用カスタムフック
 *
 * @description
 * MonthlyDoctorsShiftページの状態管理とビジネスロジックを統合する。
 * 日/週/月の表示切り替え、CRUD操作を提供。
 *
 * @param {DoctorShiftRepository} repository - DIされるリポジトリ実装
 * @param {Date} selectedDate - 選択中の日付
 * @param {ViewMode} viewMode - 表示モード
 * @returns {UseMonthlyDoctorsShiftReturn} 状態とアクション関数
 *
 * @example
 * const { shifts, isLoading, fetchShifts } = useMonthlyDoctorsShift(repository, selectedDate, viewMode);
 */
export const useMonthlyDoctorsShift = (
  repository: DoctorShiftRepository,
  selectedDate: Date,
  viewMode: ViewMode
): UseMonthlyDoctorsShiftReturn => {
  const [state, setState] = useState<MonthlyDoctorsShiftState>({
    shifts: [],
    isLoading: false,
    error: null,
    editingShift: null,
    isPopupOpen: false,
    isCreating: false,
  });

  /**
   * 表示モードに応じた日付範囲を計算
   */
  const dateRange = useMemo(
    () => calculateDateRange(selectedDate, viewMode),
    [selectedDate, viewMode]
  );

  /**
   * シフトデータを取得
   */
  const fetchShifts = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const result = await fetchDoctorShiftsUseCase(repository, dateRange);

    if (result.success) {
      setState((prev) => ({
        ...prev,
        shifts: result.shifts,
        isLoading: false,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.error || 'データの取得に失敗しました',
      }));
    }
  }, [repository, dateRange]);

  // 日付範囲変更時にデータ再取得
  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  /**
   * 表示期間の日付配列を取得
   */
  const getDatesInRange = useCallback((date: Date, mode: ViewMode): Date[] => {
    const dates: Date[] = [];
    const range = calculateDateRange(date, mode);
    let current = new Date(range.startDate);
    const end = new Date(range.endDate);

    while (current <= end) {
      dates.push(new Date(current));
      current = addDays(current, 1);
    }

    return dates;
  }, []);

  /**
   * 次の期間へ移動時の日付を計算
   */
  const getNextDate = useCallback((currentDate: Date, mode: ViewMode): Date => {
    switch (mode) {
      case 'day':
        return addDays(currentDate, 1);
      case 'week':
      case 'slotSummaryWeek':
        return addDays(currentDate, 7);
      case 'month':
      case 'slotSummaryMonth':
        return addMonths(currentDate, 1);
    }
  }, []);

  /**
   * 前の期間へ移動時の日付を計算
   */
  const getPreviousDate = useCallback((currentDate: Date, mode: ViewMode): Date => {
    switch (mode) {
      case 'day':
        return subDays(currentDate, 1);
      case 'week':
      case 'slotSummaryWeek':
        return subDays(currentDate, 7);
      case 'month':
      case 'slotSummaryMonth':
        return subMonths(currentDate, 1);
    }
  }, []);

  /**
   * シフトバークリック
   */
  const handleShiftClick = useCallback((shift: DoctorShift) => {
    setState((prev) => ({
      ...prev,
      editingShift: shift,
      isPopupOpen: true,
      isCreating: false,
    }));
  }, []);

  /**
   * タイムラインダブルクリック
   */
  const handleTimelineDoubleClick = useCallback((date: string, time: string) => {
    setState((prev) => ({
      ...prev,
      editingShift: {
        id: '',
        doctor_id: '',
        shift_date: date,
        start_time: time,
        end_time: '',
        created_at: '',
        updated_at: '',
      },
      isPopupOpen: true,
      isCreating: true,
    }));
  }, []);

  /**
   * ポップアップを閉じる
   */
  const closePopup = useCallback(() => {
    setState((prev) => ({
      ...prev,
      editingShift: null,
      isPopupOpen: false,
      isCreating: false,
    }));
  }, []);

  /**
   * シフト保存
   */
  const saveShift = useCallback(
    async (data: Partial<DoctorShift>): Promise<boolean> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      let result: OperationResult<DoctorShift>;
      if (state.isCreating) {
        result = await createDoctorShiftUseCase(repository, {
          doctor_id: data.doctor_id!,
          shift_date: data.shift_date!,
          start_time: data.start_time!,
          end_time: data.end_time!,
          slot_type: data.slot_type,
        });
      } else {
        result = await updateDoctorShiftUseCase(repository, {
          id: state.editingShift!.id,
          doctor_id: data.doctor_id,
          shift_date: data.shift_date,
          start_time: data.start_time,
          end_time: data.end_time,
          slot_type: data.slot_type,
          version: state.editingShift!.version || 0,
        });
      }

      if (result.success) {
        await fetchShifts();
        closePopup();
        return true;
      } else {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: result.error || '保存に失敗しました',
        }));
        return false;
      }
    },
    [repository, state.isCreating, state.editingShift, fetchShifts, closePopup]
  );

  /**
   * シフト削除
   */
  const deleteShift = useCallback(
    async (id: string, version: number): Promise<boolean> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const result = await deleteDoctorShiftUseCase(repository, { id, version });

      if (result.success) {
        await fetchShifts();
        closePopup();
        return true;
      } else {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: result.error || '削除に失敗しました',
        }));
        return false;
      }
    },
    [repository, fetchShifts, closePopup]
  );

  /**
   * エラークリア
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchShifts,
    getDatesInRange,
    getNextDate,
    getPreviousDate,
    handleShiftClick,
    handleTimelineDoubleClick,
    closePopup,
    saveShift,
    deleteShift,
    clearError,
  };
};
